import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="import-form"
export default class extends Controller {
  static targets = [
    "newCampaignField", 
    "existingCampaignField", 
    "heyreachInput", 
    "instantlyInput",
    "campaignNameInput",
    "existingCampaignSelect", 
    "campaignOptionRadio",
    "submitButton"
  ]
  static values = { campaignMappingsUrl: String }

  connect() {
    // Set initial state based on which radio button is checked
    this.setInitialState()
    // Validate form on connect
    this.validateForm()
  }

  setInitialState() {
    // Find which radio button is checked - try both possible name formats
    let checkedRadio = this.element.querySelector('input[name="campaign_option"]:checked')
    if (!checkedRadio) {
      checkedRadio = this.element.querySelector('input[name*="campaign_option"]:checked')
    }
    const selectedOption = checkedRadio ? checkedRadio.value : "new"
    
    this.updateFieldVisibility(selectedOption)
  }

  toggleCampaignOption(event) {
    const selectedOption = event.target.value
    this.updateFieldVisibility(selectedOption)
  }

  updateFieldVisibility(selectedOption) {
    if (selectedOption === "new") {
      if (this.hasNewCampaignFieldTarget) {
        this.newCampaignFieldTarget.classList.remove("hidden")
      }
      if (this.hasExistingCampaignFieldTarget) {
        this.existingCampaignFieldTarget.classList.add("hidden")
      }
      // Clear platform mappings when creating new campaign
      this.clearPlatformMappings()
    } else if (selectedOption === "existing") {
      if (this.hasNewCampaignFieldTarget) {
        this.newCampaignFieldTarget.classList.add("hidden")
      }
      if (this.hasExistingCampaignFieldTarget) {
        this.existingCampaignFieldTarget.classList.remove("hidden")
      }
    }
  }

  // Handle existing campaign selection change
  existingCampaignChanged(event) {
    const campaignId = event.target.value
    if (campaignId) {
      this.fetchCampaignMappings(campaignId)
    } else {
      this.clearPlatformMappings()
    }
  }

  // Fetch campaign mappings via AJAX
  async fetchCampaignMappings(campaignId) {
    if (!this.hasCampaignMappingsUrlValue) {
      console.warn("Campaign mappings URL not configured")
      return
    }

    try {
      const url = `${this.campaignMappingsUrlValue}?campaign_id=${campaignId}`
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.populatePlatformMappings(data)
      } else {
        console.error("Failed to fetch campaign mappings")
        this.clearPlatformMappings()
      }
    } catch (error) {
      console.error("Error fetching campaign mappings:", error)
      this.clearPlatformMappings()
    }
  }

  // Populate platform mapping fields
  populatePlatformMappings(mappings) {
    if (this.hasHeyreachInputTarget && mappings.heyreach) {
      this.heyreachInputTarget.value = mappings.heyreach
    }
    if (this.hasInstantlyInputTarget && mappings.instantly) {
      this.instantlyInputTarget.value = mappings.instantly
    }
  }

  // Clear platform mapping fields
  clearPlatformMappings() {
    if (this.hasHeyreachInputTarget) {
      this.heyreachInputTarget.value = ""
    }
    if (this.hasInstantlyInputTarget) {
      this.instantlyInputTarget.value = ""
    }
  }

  // Form validation - called when campaign fields change
  validateForm() {
    if (this.hasSubmitButtonTarget) {
      const isValidCampaign = this.isValidCampaignSelected()

      const isFormValid = isValidCampaign
      
      this.submitButtonTarget.disabled = !isFormValid
      
      if (isFormValid) {
        this.submitButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
        this.submitButtonTarget.classList.add('hover:bg-theme-accent/80', 'cursor-pointer')
      } else {
        this.submitButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
        this.submitButtonTarget.classList.remove('hover:bg-theme-accent/80', 'cursor-pointer')
      }
    }
  }

  // Check if a valid campaign is selected
  isValidCampaignSelected() {
    // Check if we have radio buttons (existing campaigns available)
    if (this.hasCampaignOptionRadioTarget && this.campaignOptionRadioTargets.length > 0) {
      const checkedRadio = this.campaignOptionRadioTargets.find(radio => radio.checked)
      
      if (!checkedRadio) return false
      
      if (checkedRadio.value === "new") {
        // For new campaign, check if name is entered
        if (this.hasCampaignNameInputTarget) {
          const campaignName = this.campaignNameInputTarget.value.trim()
          return campaignName.length > 0
        }
      } else if (checkedRadio.value === "existing") {
        // For existing campaign, check if one is selected
        if (this.hasExistingCampaignSelectTarget) {
          const selectedCampaign = this.existingCampaignSelectTarget.value
          return selectedCampaign !== "" && selectedCampaign !== null
        }
      }
    } else {
      // No radio buttons - this is the "no existing campaigns" case
      // Just check if campaign name is entered
      if (this.hasCampaignNameInputTarget) {
        const campaignName = this.campaignNameInputTarget.value.trim()
        return campaignName.length > 0
      }
    }
    
    return false
  }

  // Check if a file is selected (delegate to file-upload controller)
  hasFileSelected() {
    const fileInput = this.element.querySelector('input[type="file"]')
    return fileInput && fileInput.files && fileInput.files.length > 0
  }
}
